import PageLayout from "@/components/feature/PageLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import PageHero from "@/components/feature/PageHero";
import PageCTA from "@/components/feature/PageCTA";
import AnimatedSection from "@/components/AnimatedSection";
import { lifeBenefitsData, lifeValuesData, lifeGalleryData } from "@/mocks/companyPageData";

export default function LifeAtAstroJalPage() {
  return (
    <PageLayout>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Company" },
          { label: "Life at AstroJal" },
        ]}
      />

      <PageHero
        badge="Life at AstroJal"
        headline="A Culture Built on Craft"
        subtext="We're a team that cares about the details — in the software we build and in the environment we build it in."
        primaryCTA="Join Our Team"
        secondaryCTA="See Open Roles"
      />

      {/* Intro */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="container-astro">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <AnimatedSection delay={100}>
                <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-accent-500/10 border border-accent-500/20 mb-6">
                  <span className="text-xs font-semibold text-accent-600 tracking-wide uppercase">
                    Our Way of Working
                  </span>
                </span>
                <h2 className="text-3xl md:text-4xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                  Space to Do Meaningful Work
                </h2>
                <p className="text-base text-foreground-600 leading-relaxed mb-6">
                  At AstroJal, we believe great software comes from teams who feel trusted and
                  supported. We keep hierarchies flat, decisions transparent and feedback honest.
                </p>
                <p className="text-base text-foreground-600 leading-relaxed">
                  You&apos;ll work on real products with real users, surrounded by people who take pride
                  in their craft and are generous with what they know.
                </p>
              </AnimatedSection>
            </div>
            <AnimatedSection>
              <div className="relative rounded-2xl overflow-hidden border border-background-200/60">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20bright%20software%20studio%20interior%20with%20engineers%20working%20at%20desks%2C%20plants%20and%20warm%20natural%20light%2C%20relaxed%20professional%20atmosphere%2C%20clean%20minimal%20design%2C%20editorial%20photography%2C%20no%20text&width=800&height=600&seq=astrojal-life-intro&orientation=landscape"
                  alt="Life and culture at AstroJal"
                  className="w-full h-[320px] md:h-[420px] object-cover object-top"
                />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24 bg-background-100 relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-30" />
        <div className="container-astro relative z-10">
          <AnimatedSection>
            <div className="text-center mb-12 md:mb-16 max-w-2xl mx-auto">
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-accent-600 mb-3 block">
                What Matters Here
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground-950 mb-4 font-heading tracking-tight">
                The Culture We Protect
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {lifeValuesData.map((value, i) => (
              <AnimatedSection key={value.title} delay={i * 80}>
                <div className="group p-7 rounded-2xl bg-background-50 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${value.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-foreground-950 mb-2 font-heading">{value.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{value.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-12 md:mb-16 max-w-2xl mx-auto">
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-accent-600 mb-3 block">
                Perks & Benefits
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground-950 mb-4 font-heading tracking-tight">
                Looking After Our People
              </h2>
              <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                Practical support that helps you do great work and enjoy the journey along the way.
              </p>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto mt-5" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {lifeBenefitsData.map((benefit, i) => (
              <AnimatedSection key={benefit.title} delay={i * 70}>
                <div className="group flex items-start gap-4 p-6 md:p-7 rounded-2xl bg-background-100 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-11 h-11 shrink-0 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${benefit.icon} text-lg`} />
                    </span>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-foreground-950 mb-1.5 font-heading">
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{benefit.desc}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 md:py-24 bg-background-100 relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-30" />
        <div className="container-astro relative z-10">
          <AnimatedSection>
            <div className="text-center mb-12 md:mb-16">
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-accent-600 mb-3 block">
                A Glimpse Inside
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground-950 mb-4 font-heading tracking-tight">
                Moments From the Team
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {lifeGalleryData.map((item, i) => (
              <AnimatedSection key={item.title} delay={i * 100}>
                <div className="group rounded-2xl overflow-hidden bg-background-50 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-full h-[220px] overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-base font-bold text-foreground-950 mb-2 font-heading">{item.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <PageCTA
        heading="Come Build With Us"
        text="If you enjoy solving real problems with people who care about the craft, we'd love to meet you."
        primaryCTA="View Open Roles"
        secondaryCTA="Contact Our Team"
      />
    </PageLayout>
  );
}